/**
 * @fileoverview Reusable per-element typography editor ("🎨 Style") shared by the document
 * section editors and the canvas property panel.
 * @module ui-admin/template-builder/documentSectionEditors/ElementStyleControls
 *
 * Responsibilities: - Render a compact, toggleable typography panel that edits a single
 *                     ElementStyle (font family/size/weight/style/color/highlight/align/lineHeight).
 *                   - Surface a "🎨 Style" affordance so the host editor stays uncluttered.
 * Dependencies:     - document-render (ElementStyle type); lucide-react (icons).
 * Architecture:     - Presentation-only, controlled. Emits `undefined` when the style is cleared
 *                     so callers can drop the key entirely (renderer falls back to theme defaults).
 * Notes:            - Per-element overrides always beat the document theme (Phase E styling).
 */

import { useState } from 'react';
import { Palette, RotateCcw, Bold, Italic, Underline } from 'lucide-react';
import type { ElementStyle } from '@firesquirrel/document-render';

const FONT_FAMILIES: Array<{ label: string; value: string }> = [
  { label: 'Theme default', value: '' },
  { label: 'Arial / Helvetica', value: "'Arial', 'Helvetica', sans-serif" },
  { label: 'Times New Roman', value: "'Times New Roman', 'Georgia', serif" },
  { label: 'Georgia', value: "'Georgia', serif" },
  { label: 'Courier New', value: "'Courier New', monospace" },
  { label: 'Verdana', value: "'Verdana', sans-serif" },
  { label: 'Tahoma', value: "'Tahoma', sans-serif" },
];

const ALIGNMENTS: Array<ElementStyle['align']> = ['left', 'center', 'right', 'justify'];

/**
 * @reusable ElementStyleControls
 * Usage: <ElementStyleControls label="Custom title" value={style} onChange={setStyle} />
 * Rules: presentation-only; controlled; calls onChange(undefined) when fully reset.
 */
export function ElementStyleControls({
  label,
  value,
  onChange,
  testIdPrefix,
}: {
  label: string;
  value?: ElementStyle;
  onChange: (style: ElementStyle | undefined) => void;
  testIdPrefix?: string;
}) {
  const [open, setOpen] = useState(false);
  const s = value ?? {};
  const tid = testIdPrefix ?? `style-${label.toLowerCase().replace(/\s+/g, '-')}`;

  // Patch a single field; drop the whole style object when nothing is left set.
  const patch = (next: Partial<ElementStyle>) => {
    const merged: ElementStyle = { ...s, ...next };
    (Object.keys(merged) as Array<keyof ElementStyle>).forEach((k) => {
      const v = merged[k];
      // Treat 0 as empty too: elementStyleCss() emits nothing for 0 (falsy), so persisting
      // it would light the "Style •" indicator with no visible effect.
      if (v === undefined || v === '' || v === false || v === 0) delete merged[k];
    });
    onChange(Object.keys(merged).length ? merged : undefined);
  };

  const hasStyle = Object.keys(s).length > 0;

  return (
    <div className="mt-1.5">
      <button
        type="button"
        data-testid={`${tid}-toggle`}
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1 rounded-sm border px-2 py-1 text-[11px] ${hasStyle ? 'border-orange-300 bg-orange-50 text-orange-700' : 'border-gray-300 bg-white text-gray-600'} hover:border-orange-400`}
      >
        <Palette className="h-3 w-3" /> Style{hasStyle ? ' •' : ''}
      </button>

      {open && (
        <div data-testid={`${tid}-panel`} className="mt-1.5 grid grid-cols-2 gap-2 rounded-md border border-gray-200 bg-gray-50 p-2">
          <label className="col-span-2 flex flex-col gap-0.5 text-[11px] text-gray-600">
            Font
            <select
              data-testid={`${tid}-font`}
              value={s.fontFamily ?? ''}
              onChange={(e) => patch({ fontFamily: e.target.value || undefined })}
              className="rounded-sm border border-gray-300 px-1 py-0.5 text-xs"
            >
              {FONT_FAMILIES.map((f) => (
                <option key={f.label} value={f.value}>{f.label}</option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-0.5 text-[11px] text-gray-600">
            Size (pt)
            <input
              type="number" min={6} max={96}
              data-testid={`${tid}-size`}
              value={s.fontSize ?? ''}
              onChange={(e) => patch({ fontSize: e.target.value ? Number(e.target.value) : undefined })}
              className="rounded-sm border border-gray-300 px-1 py-0.5 text-xs"
            />
          </label>

          <label className="flex flex-col gap-0.5 text-[11px] text-gray-600">
            Line height
            <input
              type="number" min={0.8} max={3} step={0.1}
              data-testid={`${tid}-lineheight`}
              value={s.lineHeight ?? ''}
              onChange={(e) => patch({ lineHeight: e.target.value ? Number(e.target.value) : undefined })}
              className="rounded-sm border border-gray-300 px-1 py-0.5 text-xs"
            />
          </label>

          <label className="flex flex-col gap-0.5 text-[11px] text-gray-600">
            Text colour
            <input
              type="color"
              data-testid={`${tid}-color`}
              value={s.color ?? '#000000'}
              onChange={(e) => patch({ color: e.target.value })}
              className="h-6 cursor-pointer rounded-sm border border-gray-300 p-px"
            />
          </label>

          <label className="flex flex-col gap-0.5 text-[11px] text-gray-600">
            Highlight
            <input
              type="color"
              data-testid={`${tid}-highlight`}
              value={s.highlight ?? '#ffffff'}
              onChange={(e) => patch({ highlight: e.target.value })}
              className="h-6 cursor-pointer rounded-sm border border-gray-300 p-px"
            />
          </label>

          <div className="col-span-2 flex items-center gap-1">
            {([
              ['bold', Bold] as const,
              ['italic', Italic] as const,
              ['underline', Underline] as const,
            ]).map(([key, Icon]) => (
              <button
                key={key}
                type="button"
                data-testid={`${tid}-${key}`}
                aria-pressed={Boolean(s[key])}
                onClick={() => patch({ [key]: !s[key] } as Partial<ElementStyle>)}
                className={`flex h-6 w-6 items-center justify-center rounded-sm border ${s[key] ? 'border-orange-400 bg-orange-100 text-orange-700' : 'border-gray-300 bg-white text-gray-600'}`}
              >
                <Icon className="h-3 w-3" />
              </button>
            ))}
            <span className="ml-2 text-[11px] text-gray-500">Align</span>
            {ALIGNMENTS.map((a) => (
              <button
                key={a}
                type="button"
                data-testid={`${tid}-align-${a}`}
                aria-pressed={s.align === a}
                onClick={() => patch({ align: s.align === a ? undefined : a })}
                className={`h-6 rounded-sm border px-1.5 text-[10px] capitalize ${s.align === a ? 'border-orange-400 bg-orange-100 text-orange-700' : 'border-gray-300 bg-white text-gray-600'}`}
              >
                {a}
              </button>
            ))}
          </div>

          <button
            type="button"
            data-testid={`${tid}-reset`}
            onClick={() => { onChange(undefined); }}
            className="col-span-2 mt-0.5 inline-flex items-center justify-center gap-1 rounded-sm border border-gray-300 bg-white py-1 text-[11px] text-gray-600 hover:bg-gray-100"
          >
            <RotateCcw className="h-3 w-3" /> Reset to theme default
          </button>
        </div>
      )}
    </div>
  );
}

export default ElementStyleControls;
